import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import type {AppFeatures} from "@khan/analytics-growthbook/app-features";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {AsyncCreateActivityAssignmentModal} from "@khan/assignments-async-loader";
import {getPass} from "@khan/data-cookies";
import {useActivityData} from "@khan/guide-core";

interface Props {
    contentDescriptor: string;
    size: "small" | "medium";
    handleAssignSuccess?: () => void;
}

const writingCoachContentID = "x1e6d165d54a37115";

const CreateActivityAssignmentButton = (
    props: Props,
): React.ReactElement | null => {
    // Hooks
    const {contentDescriptor} = props;
    const [activityKind, activityID] = contentDescriptor.split(":");
    const activityDCULData = useActivityData(activityID, activityKind);
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    const [showModal, setShowModal] = React.useState(false);
    const navigateToEditScreen =
        (useFeatureIsOn<AppFeatures>("assign-from-explore-tool-cards") ||
            getPass("assign-from-explore-tool-cards")) &&
        activityID !== writingCoachContentID;

    const handleAssignSuccess = () => {
        props.handleAssignSuccess?.();
    };
    const handleAssignClose = () => {
        setShowModal(false);
    };

    const href = `/teacher/edit-your-assignment/${activityID}`;
    const handleAssignButtonClick = () => {
        if (!navigateToEditScreen) {
            setShowModal(true);
        }
        publishAnalyticsEvent({
            eventType: "AiGuideActivityAssignButtonClicked",
            eventSchemaVersion: 1,
            activityId: activityID,
        });
    };

    // Only show the Create Assignment button for valid AIGuideActivity content
    // NOTE: We are not checking whether this specific activity is assignable
    // as we are checking that upstream, but if we want to add that check here
    // to be more defensive, we can do so.
    if (
        activityDCULData.status !== "ready" ||
        activityDCULData.contentKind !== "AIGuideActivity" ||
        !activityDCULData.contentId
    ) {
        return null;
    }

    const {domainId, courseId, unitId, lessonId, contentKind, contentId} =
        activityDCULData;

    return (
        <>
            {showModal && (
                <AsyncCreateActivityAssignmentModal
                    contentDescriptor={contentDescriptor}
                    contentPath={{
                        domainId,
                        courseId,
                        unitId,
                        lessonId,
                        contentKind,
                        contentId,
                    }}
                    onClose={handleAssignClose}
                    onSuccess={handleAssignSuccess}
                />
            )}
            {navigateToEditScreen ? (
                <Button
                    kind="primary"
                    size={props.size}
                    onClick={handleAssignButtonClick}
                    href={href}
                >
                    {t`Assign`}
                </Button>
            ) : (
                <Button
                    kind="primary"
                    size={props.size}
                    onClick={handleAssignButtonClick}
                >
                    {t`Assign`}
                </Button>
            )}
        </>
    );
};

export default CreateActivityAssignmentButton;
